import styled from 'styled-components';
import { Dispatch, MouseEventHandler, SetStateAction } from 'react';

import { formatDate } from '@services/utils/DateUtils';
import { NumberBadge, OptionItem, OptionItemInput, OptionItemLabel } from '@components/style';
import Search from '@components/shared/filters/search/Search';
import { FilterRecallItemType } from '@appTypes/FilterTypes';

type RecallDropdownMobileProps = {
  filteredItems: FilterRecallItemType[];
  searchText: string;
  setSearchText: Dispatch<SetStateAction<string>>;
  isChecked: (item: FilterRecallItemType) => boolean;
  handleChange: (item: FilterRecallItemType) => void;
  toggleSelection: (selectAll: boolean) => void;
  reset: MouseEventHandler<HTMLButtonElement>;
};

const RecallDropdownMobile = ({ filteredItems, isChecked, searchText, setSearchText, handleChange, reset, toggleSelection }: RecallDropdownMobileProps) => {
  return (
    <>
      <Search placeholder={'Filter'} search={searchText} setSearch={setSearchText} type={'text'} />
      {filteredItems.length > 0 ? (
        <ItemsWrapper data-testid='recall-filter-items-mobile'>
          {filteredItems.map((item) => {
            return (
              <Row key={item.key}>
                <OptionItem key={item.key} onClick={() => handleChange(item)}>
                  <CheckboxWrapper>
                    <OptionItemInput type='checkbox' top={2} checked={isChecked(item)} onChange={() => handleChange(item)} />
                    <LabelAndDateWrapper>
                      <OptionItemLabel>{item.label}</OptionItemLabel>
                      <DateLabel>{formatDate(item.date)}</DateLabel>
                    </LabelAndDateWrapper>
                  </CheckboxWrapper>
                  <RecallBadge>{item.count}</RecallBadge>
                </OptionItem>
              </Row>
            );
          })}
        </ItemsWrapper>
      ) : (
        <NoResults data-testid='recall-filter-no-results-mobile'>
          <span>No Results</span>
          <Button onClick={reset}>Click to reset</Button>
        </NoResults>
      )}
      <ButtonWrapper data-testid='recall-filter-buttons-wrapper-mobile'>
        <span>
          <Button onClick={() => toggleSelection(true)}>Select</Button> / <Button onClick={() => toggleSelection(false)}>Deselect All</Button>
        </span>
        <Button data-testid='reset-link' disabled={searchText === ''} onClick={reset}>
          Reset
        </Button>
      </ButtonWrapper>
    </>
  );
};

export default RecallDropdownMobile;

const Row = styled.li`
  display: flex;
  flex-direction: column;
`;

const DateLabel = styled.p`
  ${({ theme }) =>
    ` color: ${theme.palette.basicGray};
      padding: 0;
      margin: 0;
      font-size: 12px;
      line-height: 120%;
  `}
`;

const ItemsWrapper = styled.ul`
  ${({ theme }) => `
    height: 200px;
    overflow-y: scroll;
    overflow-x: hidden;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-inline: none;
    padding-left: 0;
    list-style: none;

    &::-webkit-scrollbar {
    width: 6px;
    }
    &::-webkit-scrollbar-thumb {
    background: ${theme.palette.darkGray};
    border-radius: 4px;
    }
    &::-webkit-scrollbar-track {
        -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
        border-radius: 8px;
    }
`}
`;

const CheckboxWrapper = styled.span`
  display: flex;
  width: 100% !important;
  align-items: flex-start;
`; //override internal class in the styled component

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 20px;
`;

type ButtonProps = {
  disabled?: boolean;
};

const Button = styled.button<ButtonProps>`
  all: unset;
  color: ${({ theme, disabled }) => (disabled ? theme.palette.darkGrayFour : theme.palette.blue)};
  font-weight: 600;
`;

const NoResults = styled.div`
  height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: 5px;
`;

export const RecallBadge = styled(NumberBadge)`
  border-radius: 5px;
`;

const LabelAndDateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
